import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Form, Input, Button, Select, message,
  Modal, Spin, Dropdown, Tooltip, Upload
} from 'antd';
import {
  PlusOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  DollarOutlined,
  RiseOutlined,
  ThunderboltFilled,
  StarFilled,
  MoreOutlined,
  ExportOutlined,
  UploadOutlined,
  ReloadOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  MailOutlined,
  HomeOutlined,
  UserOutlined,
  PictureOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import Swal from 'sweetalert2';
import {
  PromotionsGet,
  PromotionsPost,
  PromotionsUpdate,
  PromotionsRemove,
} from "../../../../Api/CoreApi";
import { BASE_API } from "../../../../Api/Api";
import './Promotions.css'; // ✅ Corrected CSS File Import

const { Option } = Select;
const { TextArea } = Input;

function Promotions() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [addOtherModalOpen, setAddOtherModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [form] = Form.useForm();
  const [otherForm] = Form.useForm();

  const [promotions, setPromotions] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);

  const baseurl = BASE_API;

  const defaultCategories = {
    venue: { color: "#8b5cf6", bg: "#ede9fe", icon: "💍", label: "Wedding Venues" },
    photography: { color: "#3b82f6", bg: "#dbeafe", icon: "📸", label: "Photography" },
    makeup: { color: "#ec4899", bg: "#fce7f3", icon: "💄", label: "Makeup" },
    fashion: { color: "#a855f7", bg: "#f3e8ff", icon: "👗", label: "Bridal & Groom Fashion" },
    catering: { color: "#ff0055", bg: "#fff0f3", icon: "🍽️", label: "Catering" },
    decoration: { color: "#f59e0b", bg: "#fef3c7", icon: "🌸", label: "Decoration" },
    entertainment: { color: "#10b981", bg: "#d1fae5", icon: "🎵", label: "Entertainment" },
    transportation: { color: "#0ea5e9", bg: "#e0f2fe", icon: "🚗", label: "Transportation & Travel" },
    planning: { color: "#6366f1", bg: "#e0e7ff", icon: "💼", label: "Wedding Planning" },
    religious: { color: "#f97316", bg: "#ffedd5", icon: "🛕", label: "Religious Services" },
  };

  const categories = { ...defaultCategories };
  customCategories.forEach((cat) => {
    categories[cat.key] = cat;
  });

  useEffect(() => {
    fetchPromotions();
    loadCustomCategories();
  }, []);

  const loadCustomCategories = () => {
    try {
      const saved = localStorage.getItem("vendor_custom_categories");
      if (saved) {
        setCustomCategories(JSON.parse(saved));
      }
    } catch (error) {
      console.error("Error loading custom categories:", error);
    }
  };

  const saveCustomCategories = (cats) => {
    try {
      localStorage.setItem("vendor_custom_categories", JSON.stringify(cats));
    } catch (error) {
      console.error("Error saving custom categories:", error);
    }
  };

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const data = await PromotionsGet();
      if (data && data.length > 0) {
        const formatted = data.map((item) => ({
          id: item.id,
          vendor_name: item.vendor_name || "Vendor Name",
          business_name: item.business_name || item.vendor_name || "Business Name",
          category: item.category || "venue",
          description: item.description || "No description",
          image: item.image ? (item.image.startsWith("http") ? item.image : `${baseurl}${item.image}`) : null,
          phone: item.phone || "",
          email: item.email || "",
          city: item.city || "",
          address: item.address || "",
          starting_price: item.starting_price || 0,
          status: item.status || "active",
          views: parseInt(item.views) || 0,
          clicks: parseInt(item.clicks) || 0,
          revenue: parseFloat(item.revenue) || 0,
          featured: item.featured || false,
        }));
        setPromotions(formatted);
      } else {
        setPromotions([]);
      }
    } catch (error) {
      setPromotions([]);
      message.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPromotions();
    setRefreshing(false);
    message.success("Data refreshed");
  };

  const getCategoryConfig = (category) => categories[category] || defaultCategories.venue;

  const filteredPromotions = promotions
    .filter((promo) => {
      const matchesSearch =
        promo.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promo.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promo.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promo.city.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === "all" || promo.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => b.featured - a.featured);

  const handleAdd = () => {
    setEditingPromo(null);
    setImageFile(null);
    setPreviewImage(null);
    setGalleryFiles([]);
    setGalleryPreviews([]);
    form.resetFields();
    form.setFieldsValue({ status: "active" });
    setModalOpen(true);
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setImageFile(null);
    setPreviewImage(promo.image);
    setGalleryFiles([]);
    setGalleryPreviews([]);
    form.setFieldsValue({
      vendor_name: promo.vendor_name,
      business_name: promo.business_name,
      category: promo.category,
      description: promo.description,
      phone: promo.phone,
      email: promo.email,
      city: promo.city,
      address: promo.address,
      starting_price: promo.starting_price,
      status: promo.status,
      featured: promo.featured,
    });
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete Vendor?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff0055",
      cancelButtonColor: "#94a3b8",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await PromotionsRemove(id);
          setPromotions(promotions.filter((p) => p.id !== id));
          message.success("Vendor deleted");
        } catch (error) {
          setPromotions(promotions.filter((p) => p.id !== id));
          message.warning("Deleted locally");
        }
      }
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });

      if (imageFile) formData.append("image", imageFile);
      galleryFiles.forEach((file, index) => {
        formData.append(`gallery_${index}`, file);
      });

      if (editingPromo) {
        try {
          const response = await PromotionsUpdate(editingPromo.id, formData);
          const updatedPromo = {
            ...editingPromo,
            ...values,
            image: response?.image
              ? (response.image.startsWith("http") ? response.image : `${baseurl}${response.image}`)
              : previewImage || editingPromo.image,
          };
          setPromotions(promotions.map((p) => p.id === editingPromo.id ? updatedPromo : p));
          message.success("Vendor updated");
        } catch (error) {
          setPromotions(promotions.map((p) =>
            p.id === editingPromo.id ? { ...p, ...values, image: previewImage || p.image } : p
          ));
          message.warning("Updated locally");
        }
      } else {
        try {
          const response = await PromotionsPost(formData);
          const newPromo = {
            id: response?.id || Date.now(),
            ...values,
            image: response?.image
              ? (response.image.startsWith("http") ? response.image : `${baseurl}${response.image}`)
              : previewImage || "https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=400",
            views: 0,
            clicks: 0,
            revenue: 0,
          };
          setPromotions([newPromo, ...promotions]);
          message.success("Vendor added");
        } catch (error) {
          const newPromo = {
            id: Date.now(),
            ...values,
            image: previewImage || "https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=400",
            views: 0,
            clicks: 0,
            revenue: 0,
          };
          setPromotions([newPromo, ...promotions]);
          message.warning("Added locally");
        }
      }

      setModalOpen(false);
      form.resetFields();
      setImageFile(null);
      setPreviewImage(null);
      setGalleryFiles([]);
      setGalleryPreviews([]);
    } catch (error) {
      message.error("Please fill all required fields");
    } finally {
      setSaving(false);
    }
  };

  const handleAddOtherCategory = async () => {
    try {
      const values = await otherForm.validateFields();
      const key = values.label.toLowerCase().replace(/\s+/g, "_");

      if (categories[key]) {
        message.warning("Category already exists!");
        return;
      }

      const newCategory = {
        key: key,
        icon: values.icon || "📌",
        label: values.label,
        color: values.color || "#64748b",
        bg: values.color ? `${values.color}20` : "#f1f5f9",
      };

      const updated = [...customCategories, newCategory];
      setCustomCategories(updated);
      saveCustomCategories(updated);

      message.success(`✅ Category "${values.label}" added!`);
      setAddOtherModalOpen(false);
      otherForm.resetFields();
    } catch (error) {
      message.error("Please fill all fields");
    }
  };

  const handleDeleteCustomCategory = (key) => {
    Swal.fire({
      title: "Delete Category?",
      text: "This will remove the category from the list",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff0055",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        const updated = customCategories.filter((c) => c.key !== key);
        setCustomCategories(updated);
        saveCustomCategories(updated);
        message.success("Category removed");
      }
    });
  };

  const handleImageUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) { message.error("Only images allowed!"); return false; }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) { message.error("Max 5MB allowed!"); return false; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
    return false;
  };

  const handleGalleryUpload = (file) => {
    if (galleryFiles.length >= 6) { message.warning("Max 6 gallery images!"); return false; }
    setGalleryFiles([...galleryFiles, file]);
    const reader = new FileReader();
    reader.onload = () => setGalleryPreviews([...galleryPreviews, reader.result]);
    reader.readAsDataURL(file);
    return false;
  };

  const removeGalleryImage = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  const handleExport = () => {
    if (promotions.length === 0) {
      message.warning("No data to export");
      return;
    }
    const headers = ["ID", "Vendor", "Business", "Category", "City", "Phone", "Email", "Price", "Status"];
    const rows = promotions.map((p) => [p.id, p.vendor_name, p.business_name, p.category, p.city, p.phone, p.email, p.starting_price, p.status]);
    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vendors_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    message.success("Exported to CSV");
  };

  const getCTR = (promo) => promo.views === 0 ? 0 : ((promo.clicks / promo.views) * 100).toFixed(1);
  const formatCurrency = (amount) => amount >= 100000 ? `₹${(amount / 100000).toFixed(1)}L` : `₹${(amount / 1000).toFixed(1)}K`;
  const formatNumber = (num) => num >= 1000 ? `${(num / 1000).toFixed(1)}K` : num.toString();

  const stats = {
    total: promotions.length,
    active: promotions.filter((p) => p.status === "active").length,
    totalViews: promotions.reduce((sum, p) => sum + p.views, 0),
    totalRevenue: promotions.reduce((sum, p) => sum + p.revenue, 0),
    avgCTR: promotions.length > 0
      ? promotions.reduce((sum, p) => sum + parseFloat(getCTR(p)), 0) / promotions.length
      : 0,
  };

  return (
    <div className="promo-page">
      {/* ============= HEADER ============= */}
      <div className="promo-topbar">
        <div className="promo-topbar-left">
          <div className="promo-topbar-icon">
            <ShopOutlined />
          </div>
          <div className="promo-topbar-text">
            <h1>Wedding Vendors <span className="promo-count">{filteredPromotions.length}</span></h1>
            <p className="desktop-only-text">Manage all wedding & event service vendors</p>
          </div>
        </div>
        <div className="promo-topbar-actions">
          <button className="promo-btn-ghost" onClick={handleRefresh} disabled={refreshing}>
            <ReloadOutlined spin={refreshing} className="btn-icon" /> 
            <span className="btn-text">Refresh</span>
          </button>
          <button className="promo-btn-ghost" onClick={handleExport}>
            <ExportOutlined className="btn-icon" /> 
            <span className="btn-text">Export</span>
          </button>
          <button className="promo-btn-primary" onClick={handleAdd}>
            <PlusOutlined className="btn-icon" /> 
            <span className="btn-text">Add Vendor</span>
          </button>
        </div>
      </div>

      {/* ============= STATS ============= */}
      <div className="promo-stats">
        <div className="promo-stat">
          <div className="promo-stat-ic">
            <ShopOutlined />
          </div>
          <div className="promo-stat-content">
            <div className="promo-stat-name">Total Vendors</div>
            <div className="promo-stat-value">{stats.total}</div>
            <span className="promo-stat-badge up">
              <ArrowUpOutlined /> {stats.active} Active
            </span>
          </div>
        </div>

        <div className="promo-stat">
          <div className="promo-stat-ic">
            <DollarOutlined />
          </div>
          <div className="promo-stat-content">
            <div className="promo-stat-name">Total Revenue</div>
            <div className="promo-stat-value">{formatCurrency(stats.totalRevenue)}</div>
            <span className="promo-stat-badge up">
              <ArrowUpOutlined /> 24%
            </span>
          </div>
        </div>

        <div className="promo-stat">
          <div className="promo-stat-ic">
            <EyeOutlined />
          </div>
          <div className="promo-stat-content">
            <div className="promo-stat-name">Total Views</div>
            <div className="promo-stat-value">{formatNumber(stats.totalViews)}</div>
            <span className="promo-stat-badge up">
              <ArrowUpOutlined /> 8%
            </span>
          </div>
        </div>

        <div className="promo-stat">
          <div className="promo-stat-ic">
            <RiseOutlined />
          </div>
          <div className="promo-stat-content">
            <div className="promo-stat-name">Avg CTR</div>
            <div className="promo-stat-value">{stats.avgCTR.toFixed(1)}%</div>
            <span className="promo-stat-badge up">
              <ArrowUpOutlined /> 5%
            </span>
          </div>
        </div>
      </div>

      {/* ============= FILTERS ============= */}
      <div className="promo-filters">
        <div className="promo-search-box">
          <SearchOutlined className="promo-search-ic" />
          <input
            type="text"
            placeholder="Search vendors, business, cities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="promo-search-clear" onClick={() => setSearchQuery("")}>×</button>
          )}
        </div>

        <div className="promo-filter-group">
          <Select
            value={filterCategory}
            onChange={setFilterCategory}
            className="promo-select"
            placeholder="Category"
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ 
                  padding: '8px', 
                  borderTop: '1px solid #f1f5f9',
                  background: '#fafbfc'
                }}>
                  <button
                    type="button"
                    onClick={() => setAddOtherModalOpen(true)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1.5px dashed #ff0055',
                      borderRadius: '6px',
                      background: '#fff0f3',
                      color: '#ff0055',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <PlusOutlined /> Add Custom Category
                  </button>
                </div>
              </>
            )}
          >
            <Option value="all">All Categories</Option>
            {Object.entries(categories).map(([key, cat]) => (
              <Option key={key} value={key}>
                {cat.icon} {cat.label}
              </Option>
            ))}
          </Select>

          {/* Hide Grid/List Toggle on Mobile via CSS (.promo-view-switch) */}
          <div className="promo-view-switch">
            <button className={viewMode === "grid" ? "active" : ""} onClick={() => setViewMode("grid")}>
              <AppstoreOutlined /> Grid
            </button>
            <button className={viewMode === "list" ? "active" : ""} onClick={() => setViewMode("list")}>
              <UnorderedListOutlined /> List
            </button>
          </div>
        </div>
      </div>

      {/* ============= CONTENT ============= */}
      {loading ? (
        <div className="promo-loader">
          <Spin size="large" />
          <p style={{ marginTop: 12, color: '#64748b' }}>Loading vendors...</p>
        </div>
      ) : filteredPromotions.length === 0 ? (
        <div className="promo-empty">
          <div className="promo-empty-ic">🏪</div>
          <h3>No vendors found</h3>
          <p>{searchQuery || filterCategory !== "all" ? "Try adjusting your filters" : "Add your first vendor to get started"}</p>
          <button className="promo-btn-primary" onClick={handleAdd}>
            <PlusOutlined className="btn-icon" /> <span className="btn-text">Add Vendor</span>
          </button>
        </div>
      ) : viewMode === "grid" ? (
        <div className="promo-grid">
          {filteredPromotions.map((promo) => {
            const catCfg = getCategoryConfig(promo.category);
            const ctr = getCTR(promo);

            return (
              <div key={promo.id} className="promo-card">
                <div className="promo-card-thumb">
                  <img src={promo.image} alt={promo.business_name} onError={(e) => {
                    e.target.src = "https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=400";
                  }} />
                  <div className="promo-card-thumb-top">
                    <div className="promo-card-cat-badge" style={{ background: catCfg.bg, color: catCfg.color }}>
                      {catCfg.icon} {catCfg.label}
                    </div>
                    <Dropdown
                      menu={{
                        items: [
                          { key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => handleEdit(promo) },
                          { key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(promo.id) },
                        ]
                      }}
                      placement="bottomRight"
                    >
                      <button className="promo-card-more"><MoreOutlined /></button>
                    </Dropdown>
                  </div>
                  {promo.featured && (
                    <div className="promo-card-hp"><StarFilled /> FEATURED</div>
                  )}
                  <div className={`promo-card-status-pill ${promo.status}`}>
                    <span className="dot"></span>
                    {promo.status === "active" ? "Active" : "Inactive"}
                  </div>
                </div>

                <div className="promo-card-body">
                  <h3 className="promo-card-title">{promo.business_name}</h3>
                  <p className="promo-card-vendor">
                    <UserOutlined /> {promo.vendor_name}
                  </p>
                  <p className="promo-card-desc">{promo.description}</p>

                  <div className="promo-card-info">
                    <span><EnvironmentOutlined /> {promo.city}</span>
                    <span><PhoneOutlined /> {promo.phone}</span>
                  </div>

                  <div className="promo-card-price">
                    <span className="price-label">Starting from</span>
                    <span className="price-value">₹{promo.starting_price?.toLocaleString('en-IN')}</span>
                  </div>

                  <div className="promo-card-stats">
                    <div className="promo-card-stat">
                      <EyeOutlined style={{ color: "#8b5cf6" }} />
                      <div>
                        <strong>{formatNumber(promo.views)}</strong>
                        <span>Views</span>
                      </div>
                    </div>
                    <div className="promo-card-stat">
                      <ThunderboltFilled style={{ color: "#f59e0b" }} />
                      <div>
                        <strong>{formatNumber(promo.clicks)}</strong>
                        <span>Clicks</span>
                      </div>
                    </div>
                    <div className="promo-card-stat">
                      <RiseOutlined style={{ color: "#10b981" }} />
                      <div>
                        <strong>{ctr}%</strong>
                        <span>CTR</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // LIST VIEW
        <div className="promo-list">
          {filteredPromotions.map((promo) => {
            const catCfg = getCategoryConfig(promo.category);
            const ctr = getCTR(promo);

            return (
              <div key={promo.id} className="promo-row">
                <div className="promo-row-thumb">
                  <img src={promo.image} alt={promo.business_name} onError={(e) => {
                    e.target.src = "https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=400";
                  }} />
                </div>

                <div className="promo-row-main">
                  <div className="promo-row-header">
                    <div className="promo-row-title-area">
                      <h3>{promo.business_name}</h3>
                      <p className="promo-row-vendor"><UserOutlined /> {promo.vendor_name}</p>
                      <div className="promo-row-tags">
                        <span className="promo-row-cat" style={{ background: catCfg.bg, color: catCfg.color }}>
                          {catCfg.icon} {catCfg.label}
                        </span>
                        <span className="promo-row-city">
                          <EnvironmentOutlined /> {promo.city}
                        </span>
                        <span className={`promo-row-status-badge ${promo.status}`}>
                          <span className="dot"></span> {promo.status}
                        </span>
                        {promo.featured && (
                          <span className="promo-row-hp"><StarFilled /> FEATURED</span>
                        )}
                      </div>
                    </div>
                    <div className="promo-row-actions">
                      <Tooltip title="Edit">
                        <button onClick={() => handleEdit(promo)}><EditOutlined /></button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <button className="danger" onClick={() => handleDelete(promo.id)}><DeleteOutlined /></button>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="promo-row-stats">
                    <span><PhoneOutlined /> <strong>{promo.phone}</strong></span>
                    {promo.email && <span><MailOutlined /> <strong>{promo.email}</strong></span>}
                    <span><DollarOutlined /> Starting <strong>₹{promo.starting_price?.toLocaleString('en-IN')}</strong></span>
                    <span><EyeOutlined /> <strong>{formatNumber(promo.views)}</strong> views</span>
                    <span><RiseOutlined /> <strong>{ctr}%</strong> CTR</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============= ADD/EDIT MODAL ============= */}
      <Modal
        title={
          <div className="promo-modal-title">
            <div className="promo-modal-ic"><ShopOutlined /></div>
            <div>
              <div className="promo-modal-h">{editingPromo ? "Edit Vendor" : "Add New Vendor"}</div>
              <div className="promo-modal-sh">{editingPromo ? "Update vendor details" : "Add a wedding/event service provider"}</div>
            </div>
          </div>
        }
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setImageFile(null); setPreviewImage(null); }}
        onOk={handleSave}
        okText={editingPromo ? "Save Changes" : "Add Vendor"}
        cancelText="Cancel"
        confirmLoading={saving}
        width={700}
        className="promo-modal"
      >
        <Form form={form} layout="vertical">
          {/* Cover Image */}
          <Form.Item label="Cover Image">
            <Upload beforeUpload={handleImageUpload} showUploadList={false} accept="image/*">
              <div className="promo-upload-box">
                {previewImage ? (
                  <>
                    <img src={previewImage} alt="Preview" />
                    <div className="promo-upload-overlay">Click to change</div>
                  </>
                ) : (
                  <>
                    <UploadOutlined style={{ fontSize: '24px', color: '#94a3b8', marginBottom: '8px' }} />
                    <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 600 }}>Upload cover image</span>
                    <span style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>PNG, JPG (max 5MB)</span>
                  </>
                )}
              </div>
            </Upload>
          </Form.Item>

          {/* Gallery Images */}
          <Form.Item label={`Gallery Images (${galleryPreviews.length}/6)`}>
            <div className="promo-gallery-grid">
              {galleryPreviews.map((preview, index) => (
                <div key={index} className="promo-gallery-item">
                  <img src={preview} alt={`Gallery ${index}`} />
                  <button
                    type="button"
                    className="promo-gallery-remove"
                    onClick={() => removeGalleryImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {galleryPreviews.length < 6 && (
                <Upload beforeUpload={handleGalleryUpload} showUploadList={false} accept="image/*">
                  <div className="promo-gallery-add">
                    <PictureOutlined />
                    <span>Add</span>
                  </div>
                </Upload>
              )}
            </div>
          </Form.Item>

          <div className="promo-form-row">
            <Form.Item name="vendor_name" label="Vendor Name" rules={[{ required: true }]}>
              <Input placeholder="Rajesh Sharma" prefix={<UserOutlined />} />
            </Form.Item>
            <Form.Item name="business_name" label="Business Name" rules={[{ required: true }]}>
              <Input placeholder="Royal Wedding Palace" prefix={<ShopOutlined />} />
            </Form.Item>
          </div>

          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select
              placeholder="Select category"
              showSearch
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{ 
                    padding: '8px', 
                    borderTop: '1px solid #f1f5f9',
                    background: '#fafbfc'
                  }}>
                    <button
                      type="button"
                      onClick={() => setAddOtherModalOpen(true)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1.5px dashed #ff0055',
                        borderRadius: '6px',
                        background: '#fff0f3',
                        color: '#ff0055',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      <PlusOutlined /> Add Custom Category
                    </button>
                  </div>
                </>
              )}
            >
              {Object.entries(categories).map(([key, cat]) => (
                <Option key={key} value={key}>{cat.icon} {cat.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <TextArea rows={3} placeholder="Describe your services..." />
          </Form.Item>

          <div className="promo-form-row">
            <Form.Item name="phone" label="Phone Number" rules={[{ required: true }]}>
              <Input placeholder="9876543210" prefix={<PhoneOutlined />} maxLength={10} />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input placeholder="vendor@example.com" prefix={<MailOutlined />} />
            </Form.Item>
          </div>

          <div className="promo-form-row">
            <Form.Item name="city" label="City" rules={[{ required: true }]}>
              <Input placeholder="Mumbai" prefix={<EnvironmentOutlined />} />
            </Form.Item>
            <Form.Item name="starting_price" label="Starting Price (₹)" rules={[{ required: true }]}>
              <Input type="number" placeholder="15000" prefix="₹" />
            </Form.Item>
          </div>

          <Form.Item name="address" label="Full Address">
            <Input placeholder="Full address with area, city, pincode" prefix={<HomeOutlined />} />
          </Form.Item>

          <div className="promo-form-row">
            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select placeholder="Select">
                <Option value="active">🟢 Active</Option>
                <Option value="inactive">🔴 Inactive</Option>
              </Select>
            </Form.Item>
            <Form.Item name="featured" label="Featured" valuePropName="checked">
              <Select placeholder="Select">
                <Option value={true}>🔥 Yes (Featured)</Option>
                <Option value={false}>📌 No</Option>
              </Select>
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* ============= ADD CUSTOM CATEGORY MODAL ============= */}
      <Modal
        title={
          <div className="promo-modal-title">
            <div className="promo-modal-ic"><PlusOutlined /></div>
            <div>
              <div className="promo-modal-h">Add Custom Category</div>
              <div className="promo-modal-sh">Create a new vendor category</div>
            </div>
          </div>
        }
        open={addOtherModalOpen}
        onCancel={() => { setAddOtherModalOpen(false); otherForm.resetFields(); }}
        onOk={handleAddOtherCategory}
        okText="Add Category"
        cancelText="Cancel"
        width={480}
        className="promo-modal"
      >
        <Form form={otherForm} layout="vertical">
          <Form.Item name="label" label="Category Name" rules={[{ required: true }]}>
            <Input placeholder="e.g., Wedding Cakes, Honeymoon Planning" />
          </Form.Item>

          <div className="promo-form-row">
            <Form.Item name="icon" label="Emoji Icon" rules={[{ required: true }]}>
              <Input placeholder="🎂" maxLength={2} />
            </Form.Item>
            <Form.Item name="color" label="Color">
              <Input type="color" defaultValue="#ff0055" style={{ height: '34px' }} />
            </Form.Item>
          </div>

          {/* Custom Categories List */}
          {customCategories.length > 0 && (
            <div className="promo-custom-cats-list">
              <div className="promo-custom-cats-label">Your Custom Categories:</div>
              {customCategories.map((cat) => (
                <div key={cat.key} className="promo-custom-cat-item">
                  <span>{cat.icon} {cat.label}</span>
                  <button
                    type="button"
                    className="promo-custom-cat-delete"
                    onClick={() => handleDeleteCustomCategory(cat.key)}
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default Promotions;